import React, { useEffect, useState } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import API_BASE_URL from '../../../config/api'
import Topbar from '../mini-comp/Topbar'
import Sidebar from '../mini-comp/Sidebar'
import {
    RiShieldKeyholeLine,
    RiEyeLine,
    RiEyeOffLine,
    RiLockPasswordLine,
} from 'react-icons/ri'

const TWO_FACTOR_URL = `${API_BASE_URL}/api/v1/users/two-factor/`

const getTwoFactorEnabled = (data) => {
    if (!data || typeof data !== 'object') return false
    return Boolean(
        data.enabled ??
        data.is_enabled ??
        data.two_factor_enabled ??
        data.two_factor ??
        data.is_2fa_enabled
    )
}

const TwoFactorAuthentication = () => {
    const [enabled, setEnabled] = useState(false)
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [loadingStatus, setLoadingStatus] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const getAuthHeaders = () => {
        const token = Cookies.get('token')
        return { Authorization: `Bearer ${token}` }
    }

    const fetchStatus = async () => {
        setLoadingStatus(true)
        try {
            const response = await axios.get(TWO_FACTOR_URL, {
                headers: getAuthHeaders(),
            })
            setEnabled(getTwoFactorEnabled(response.data))
        } catch (error) {
            // If GET is not supported, fall back to user cookie flags
            console.error('Error fetching 2FA status:', error)
            try {
                const userCookie = Cookies.get('user')
                const user = userCookie ? JSON.parse(userCookie) : null
                setEnabled(getTwoFactorEnabled(user))
            } catch (e) {
                setEnabled(false)
            }
        } finally {
            setLoadingStatus(false)
        }
    }

    useEffect(() => {
        fetchStatus()
    }, [])

    const updateUserCookie = (isEnabled) => {
        const userCookie = Cookies.get('user')
        if (!userCookie) return
        try {
            const user = JSON.parse(userCookie)
            Cookies.set(
                'user',
                JSON.stringify({
                    ...user,
                    two_factor_enabled: isEnabled,
                    enabled: isEnabled,
                }),
                { expires: 7, path: '/' }
            )
        } catch (e) {
            // ignore malformed cookie
        }
    }

    const handleToggle = async (e) => {
        e.preventDefault()

        if (!password.trim()) {
            Swal.fire('Error', 'Please enter your password to continue.', 'error')
            return
        }

        const nextEnabled = !enabled
        const actionLabel = nextEnabled ? 'activate' : 'deactivate'

        const confirm = await Swal.fire({
            title: `${nextEnabled ? 'Enable' : 'Disable'} Two-Factor Authentication?`,
            text: nextEnabled
                ? 'This will require an extra verification step when you sign in.'
                : 'Your account will only be protected by password after this.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `Yes, ${actionLabel}`,
            cancelButtonText: 'Cancel',
        })

        if (!confirm.isConfirmed) return

        setSubmitting(true)
        try {
            const response = await axios.post(
                TWO_FACTOR_URL,
                {
                    enabled: nextEnabled,
                    password,
                },
                { headers: getAuthHeaders() }
            )

            const responseEnabled = getTwoFactorEnabled(response.data)
            const finalEnabled =
                typeof response.data?.enabled === 'boolean' ||
                typeof response.data?.is_enabled === 'boolean' ||
                typeof response.data?.two_factor_enabled === 'boolean'
                    ? responseEnabled
                    : nextEnabled

            setEnabled(finalEnabled)
            updateUserCookie(finalEnabled)
            setPassword('')

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: finalEnabled
                    ? 'Two-factor authentication has been activated.'
                    : 'Two-factor authentication has been deactivated.',
                timer: 2000,
                showConfirmButton: false,
            })
        } catch (error) {
            console.error('Error updating 2FA:', error)
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.error ||
                error.response?.data?.password?.[0] ||
                error.response?.data?.message ||
                `Failed to ${actionLabel} two-factor authentication.`
            Swal.fire('Error', errorMessage, 'error')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row mt-3 justify-content-center">
                            <div className="col-xl-9 col-lg-10">
                                <div className="card shadow-sm border-0">
                                    <div className="card-header bg-transparent border-bottom p-4">
                                        <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                                            <RiShieldKeyholeLine className="text-primary" size={20} />
                                            Two-Factor Authentication
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        {loadingStatus ? (
                                            <div className="text-center py-4">
                                                <span
                                                    className="spinner-border text-primary"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                <p className="mt-2 mb-0 text-muted">Loading status...</p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="d-flex align-items-center justify-content-between mb-4 p-3 rounded border">
                                                    <div>
                                                        <h6 className="mb-1">Current Status</h6>
                                                        <p className="mb-0 text-muted fs-13">
                                                            {enabled
                                                                ? 'Two-factor authentication is currently enabled on your account.'
                                                                : 'Two-factor authentication is currently disabled on your account.'}
                                                        </p>
                                                    </div>
                                                    <span
                                                        className={`badge ${
                                                            enabled
                                                                ? 'bg-label-success'
                                                                : 'bg-label-secondary'
                                                        }`}
                                                    >
                                                        {enabled ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </div>

                                                <form onSubmit={handleToggle}>
                                                    <div className="mb-4">
                                                        <label className="form-label" htmlFor="password">
                                                            Account Password <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="input-group">
                                                            <span className="input-group-text">
                                                                <RiLockPasswordLine />
                                                            </span>
                                                            <input
                                                                type={showPassword ? 'text' : 'password'}
                                                                className="form-control"
                                                                id="password"
                                                                name="password"
                                                                placeholder="Enter your password to confirm"
                                                                value={password}
                                                                onChange={(e) => setPassword(e.target.value)}
                                                                required
                                                                disabled={submitting}
                                                            />
                                                            <button
                                                                className="btn btn-outline-secondary"
                                                                type="button"
                                                                onClick={() => setShowPassword((prev) => !prev)}
                                                                disabled={submitting}
                                                            >
                                                                {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                                                            </button>
                                                        </div>
                                                        <div className="form-text">
                                                            Your password is required to{' '}
                                                            {enabled ? 'deactivate' : 'activate'} two-factor
                                                            authentication.
                                                        </div>
                                                    </div>

                                                    <div className="d-flex justify-content-end">
                                                        <button
                                                            type="submit"
                                                            className={`btn px-4 d-flex align-items-center gap-2 ${
                                                                enabled ? 'btn-danger' : 'btn-primary'
                                                            }`}
                                                            disabled={submitting || !password.trim()}
                                                        >
                                                            {submitting ? (
                                                                <>
                                                                    <span
                                                                        className="spinner-border spinner-border-sm"
                                                                        role="status"
                                                                        aria-hidden="true"
                                                                    />
                                                                    {enabled ? 'Deactivating...' : 'Activating...'}
                                                                </>
                                                            ) : enabled ? (
                                                                'Deactivate Two-Factor Authentication'
                                                            ) : (
                                                                'Activate Two-Factor Authentication'
                                                            )}
                                                        </button>
                                                    </div>
                                                </form>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TwoFactorAuthentication
